import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lock, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const { loginWithCognito, isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();

  // Force light theme on login page (preserve previous behaviour).
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("light");
    return () => {
      const saved = localStorage.getItem("tychi_theme") || "light";
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(saved);
    };
  }, []);

  // If already authenticated, bounce to the role landing page.
  useEffect(() => {
    if (isAuthenticated && user) {
      const paths = { super_admin: "/super-admin", org_admin: "/org-admin/dashboard", admin: "/admin", investor: "/investor", prospective_client: "/prospective" };
      navigate(paths[user.role] || "/", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = async () => {
    try {
      // Preserve deep-link params (loginTarget, target_app, fund_id) across
      // the Cognito redirect so CallbackPage can restore them after code exchange.
      const sp = new URLSearchParams(window.location.search);
      const targetApp = sp.get("loginTarget") || sp.get("target_app");
      const deepFundId = sp.get("fund_id");
      if (targetApp) sessionStorage.setItem("tychi_target_app", targetApp);
      else sessionStorage.removeItem("tychi_target_app");
      if (deepFundId) sessionStorage.setItem("tychi_deep_fund_id", deepFundId);
      else sessionStorage.removeItem("tychi_deep_fund_id");

      await loginWithCognito();
    } catch (e) {
      // loginWithCognito navigates the browser; only surfaces if the
      // backend /auth/login-url call itself fails.
      console.error("Failed to start Cognito login", e);
    }
  };

  return (
    <div className="login-split" data-testid="login-page">
      <div className="login-image">
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
          alt="Corporate architecture"
        />
        <div className="overlay">
          <div className="animate-fade-in">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-blue-400 mb-3">
              Investor Portal
            </p>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">
              The Future of Fund Operations
            </h2>
            <p className="text-slate-400 text-sm max-w-md leading-relaxed">
              A unified platform for portfolio accounting, compliance, reporting, and secure data management.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background p-8 lg:p-16">
        <div className="w-full max-w-md animate-slide-up">
          <div className="flex items-center gap-3 mb-12">
            <img src="/tychi-icon.svg" alt="Tychi" className="w-10 h-10 rounded-xl" />
            <span className="font-heading text-2xl font-bold text-foreground tracking-tight">Tychi</span>
          </div>

          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight mb-2">
              Sign in
            </h1>
            <p className="text-muted-foreground text-sm">
              Authentication is handled securely by AWS Cognito.
            </p>
          </div>

          <div className="space-y-5">
            <Button
              type="button"
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full h-11 font-medium text-sm bg-slate-900 hover:bg-slate-800 text-white shadow-[0_0_20px_rgba(15,23,42,0.35)] active:scale-[0.98] transition-all"
              data-testid="cognito-login-button"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Continue to Secure Sign-in <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>

            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Single sign-on across all Tychi platforms.</span>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Lock className="w-3.5 h-3.5" />
              <span>This portal is invitation-only. Contact your administrator for access.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
